import { Navigate, redirect, replace, type LoaderFunctionArgs, type RouteObject } from 'react-router-dom';
import { appRoutes, BROKERAGE_AGREEMENT_PAGE_PATH, SIGNING_PAGE_PATH } from '@immo/api-contract';
import { WorkspaceLayout } from '@/components/layout/workspace-layout';
import { dashboardShouldRevalidate } from '@/pages/workspace/dashboard/dashboard.loader';
import { workspaceHomeLoader } from '@/pages/workspace/home.loader';
import { hlreAreaLoader } from '@/pages/workspace/hlre/area.loader';
import { chatLoader } from '@/pages/workspace/chat.loader';
import { mailboxHomeLoader } from '@/pages/workspace/mailbox/mailbox-home.loader';
import { calendarHomeLoader } from '@/pages/workspace/calendar/calendar-home.loader';
import { LoadingPage } from '@/pages/loading';
import { NotFoundPage } from '@/pages/not-found';
import { RouteErrorPage } from '@/pages/route-error';
import { SignInPage } from '@/pages/auth/sign-in';
import { ForbiddenError, NetworkError } from '@/lib/errors/api-error';
import { onboardingGateTarget } from '@/lib/auth/onboarding';
import { buildSignInRedirect } from '@/lib/auth/return-to';
import { fetchMe } from '@/lib/auth/session';
import {
  loadWorkspaceContext,
  loadWorkspaceShell,
  rememberWorkspaceSlug,
  resolveDefaultWorkspaceSlug,
  resolveLastWorkspaceSlug,
  type WorkspaceShell,
} from '@/lib/auth/workspace-context';
import { canonicalWorkspaceRedirect, workspacePath } from '@/lib/workspace-path';

async function requireWorkspace({ params, request }: LoaderFunctionArgs): Promise<WorkspaceShell> {
  try {
    const ctx = await loadWorkspaceShell(params.workspaceId);

    // Normalise the URL to the canonical slug (handles a renamed workspace's
    // stale human prefix, or a raw-UUID link) before anything renders, so every
    // in-app link built from the URL segment is canonical. `replace`, not a push:
    // a pushed redirect leaves the stale URL in history, so browser Back pops to
    // it and it redirects forward again — the user can never Back out.
    const canonical = canonicalWorkspaceRedirect(request, params.workspaceId, ctx.workspaceSlug);

    if (canonical !== null) {
      throw replace(canonical);
    }

    // Server-authoritative onboarding gates: the member gate (IMM-271.1) holds a
    // member who hasn't finished their personal onboarding on the welcome screen;
    // the company gate (IMM-322) then holds the workspace until its main company
    // is set up — routing an editor into the wizard and everyone else onto the
    // un-bypassable blocking screen. Member onboarding takes precedence.
    const target = onboardingGateTarget(ctx.me, ctx.workspaceId, ctx.permissions);

    if (target !== null) {
      // The onboarding gate routes are workspace-scoped too, so the member lands
      // on (and can deep-link / switch within) the onboarding for THIS workspace.
      throw redirect(workspacePath(ctx.workspaceSlug, target));
    }

    rememberWorkspaceSlug(ctx.workspaceSlug);

    return ctx;
  } catch (err) {
    if (err instanceof Response) throw err;

    // A transport failure is not an auth failure — we never got as far as
    // asking. Bouncing to `/sign-in` on an API blip evicts a signed-in member
    // from the page they were on and reads to them as "you were logged out", so
    // let it reach this route's `errorElement`, which offers the outage copy and
    // a retry (ADR 0014). Everything else still fails closed: if the session
    // genuinely cannot be verified, sign-in is the right destination.
    if (err instanceof NetworkError) throw err;

    // A member of another workspace, or a caller the workspace gives no way in, is refused — not signed out.
    if (err instanceof ForbiddenError) throw err;

    console.error('[requireWorkspace] unable to verify session; routing to /sign-in', err);
    // Preserve the deep link the user was heading to (IMM-486) so sign-in
    // returns them here rather than dropping them on the workspace home.
    const url = new URL(request.url);
    throw redirect(buildSignInRedirect(url.pathname + url.search));
  }
}

// Bare `/` carries no workspace. Resolve the workspace to pre-select — the first
// one the member can actually enter (IMM-322) — and forward to `/w/:workspaceId`,
// where `requireWorkspace` takes over (and applies the onboarding gate). A user
// with no membership falls through to the not-registered support screen.
async function rootRedirect(): Promise<Response> {
  const me = await fetchMe();

  if (me === null) {
    return redirect('/sign-in');
  }

  const workspaceSlug = resolveDefaultWorkspaceSlug(me);

  return redirect(workspaceSlug === null ? '/support/not-registered' : workspacePath(workspaceSlug));
}

// The browser extension's „Bei immoOS anmelden" (IMM-1381) opens this bare path: it knows no workspace, so the
// member lands on the Portale page's „Erweiterung verbinden" of the workspace they last entered (else the default).
async function connectExtensionRedirect(request: Request): Promise<Response> {
  const me = await fetchMe();

  if (me === null) {
    return redirect(buildSignInRedirect(new URL(request.url).pathname));
  }

  const workspaceSlug = resolveLastWorkspaceSlug(me);

  return redirect(
    workspaceSlug === null
      ? '/support/not-registered'
      : `${appRoutes.settingsPortals(workspaceSlug)}#browser-extension`,
  );
}

const topLevelRoutes: RouteObject[] = [
  {
    path: '/sign-in',
    element: <SignInPage />,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/auth/callback',
    lazy: () => import('@/pages/auth/callback').then((m) => ({ Component: m.AuthCallbackPage })),
    errorElement: <RouteErrorPage />,
  },
  {
    // Sandbox only (IMM-1084.3); always registered, and a non-sandbox backend's 404 renders the not-found page.
    path: '/sandbox/sign-in',
    lazy: () => import('@/pages/sandbox/sign-in').then((m) => ({ Component: m.SandboxSignInPage })),
    errorElement: <RouteErrorPage />,
  },
  {
    // Sandbox only (IMM-1084.3); always registered, and a non-sandbox backend's 404 renders the not-found page.
    path: '/sandbox/callback',
    lazy: () => import('@/pages/sandbox/callback').then((m) => ({ Component: m.SandboxCallbackPage })),
    errorElement: <RouteErrorPage />,
  },
  {
    // Admin impersonation landing (IMM-346). The admin console redirects here
    // with the single-use handoff code in the URL fragment; the page exchanges
    // it for the `imp_` operating credential and drops into the app as the
    // impersonated member. PUBLIC — possession of the code is the proof, like
    // the auth callback — so it sits outside `requireSession`/`requireWorkspace`.
    path: '/impersonate',
    lazy: () => import('@/pages/auth/impersonate').then((m) => ({ Component: m.ImpersonatePage })),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/sign-out',
    lazy: () => import('@/pages/auth/sign-out').then((m) => ({ Component: m.SignOutPage })),
    errorElement: <RouteErrorPage />,
  },
  {
    // The OAuth consent screen (IMM-601.8.2). The issuer redirects a member's
    // browser here from their MCP client; the loader is the sign-in bridge — an
    // anonymous arrival is bounced to `/sign-in` carrying this URL as `returnTo`
    // and lands back here signed in. Top-level and outside `requireWorkspace`
    // like `/auth/callback`: the redirect URI is registered with the issuer long
    // before anyone knows which workspace the member will be in.
    path: '/oauth/consent',
    lazy: () =>
      import('@/pages/auth/oauth-consent').then((m) => ({
        loader: m.oauthConsentLoader,
        Component: m.OauthConsentPage,
      })),
    errorElement: <RouteErrorPage />,
  },
  {
    // Nylas hosted-OAuth landing for the mailbox connect flow (IMM-86.3). The
    // redirect URI registered with the provider is this bare path (no `/w/:slug`
    // prefix), so it sits at the top level like `/auth/callback`. The page reads
    // the one-time `code` + signed `state` from the query and the workspace +
    // provider from the `sessionStorage` stash, completes the grant, then routes
    // the member back into their workspace's mailbox settings.
    path: '/settings/mailbox/callback',
    lazy: () => import('@/pages/settings/mailbox-callback').then((m) => ({ Component: m.MailboxCallbackPage })),
    errorElement: <RouteErrorPage />,
  },
  {
    // ImmoScout24 OAuth landing (IMM-1057.1). Bare path like the mailbox callback:
    // the workspace comes from the `sessionStorage` stash, not the URL.
    path: '/settings/portals/immoscout24/callback',
    lazy: () => import('@/pages/settings/portal-callback').then((m) => ({ Component: m.PortalCallbackPage })),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/connect-extension',
    loader: ({ request }: LoaderFunctionArgs) => connectExtensionRedirect(request),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/support/not-registered',
    lazy: () => import('@/pages/support/not-registered').then((m) => ({ Component: m.SupportNotRegisteredPage })),
    errorElement: <RouteErrorPage />,
  },
  {
    // Public partner self-service onboarding. The invitation e-mail points here
    // with `?token=<rawToken>` (backend MAGIC_LINK_PATH); the page exchanges the
    // token for a session itself, so this route is intentionally outside both
    // `requireSession` and the member onboarding gate. Pre-membership, so it
    // carries no workspace.
    path: '/onboarding/partner',
    lazy: () => import('@/pages/onboarding/partner').then((m) => ({ Component: m.PartnerOnboardingPage })),
    errorElement: <RouteErrorPage />,
  },
  {
    // Public member-invite entry (IMM-271). The workspace-branded invitation
    // e-mail points here with `?token=<rawToken>`; the page exchanges the token
    // for a session via the magic-link + WorkOS OTP flow, then hands off to the
    // onboarding gate. Outside `requireSession` and the onboarding gate, like
    // the partner route. Pre-membership, so it carries no workspace.
    path: '/onboarding/member',
    lazy: () => import('@/pages/onboarding/member').then((m) => ({ Component: m.MemberInvitePage })),
    errorElement: <RouteErrorPage />,
  },
  {
    // The Interessent's personal page (IMM-1065.7). Public on purpose: possession of the link is the proof, its token
    // rides in the fragment, and it sits outside `requireSession` so a buyer is never bounced to sign-in.
    path: BROKERAGE_AGREEMENT_PAGE_PATH,
    lazy: () =>
      import('@/pages/public/brokerage-agreement/page').then((m) => ({ Component: m.BrokerageAgreementPage })),
    errorElement: <RouteErrorPage />,
  },
  {
    // The Eigentümer's signing page (IMM-1171.3). Public on purpose, like the buyer page: possession of the link is the
    // proof, its token rides in the fragment, and it sits outside `requireSession` so an owner is never bounced to sign-in.
    path: SIGNING_PAGE_PATH,
    lazy: () => import('@/pages/public/signing/page').then((m) => ({ Component: m.SigningPage })),
    errorElement: <RouteErrorPage />,
  },
  {
    // The prospect's web exposé (IMM-1161.4). Public on purpose: a published exposé is meant to be opened by anyone
    // with the link, so it sits outside `requireSession` and the onboarding gate. Static first segments outrank this
    // dynamic one, and IMM-1161.1's reserved list keeps a Web-Adresse from naming them.
    path: '/:webAddress/expose/:objectNumber',
    lazy: () => import('@/pages/public/web-expose/page').then((m) => ({ Component: m.WebExposePage })),
    errorElement: <RouteErrorPage />,
  },
  // Member/company onboarding gate screens are workspace-scoped (IMM-322): they
  // sit under `/w/:workspaceId` like the app, but OUTSIDE `requireWorkspace` (the
  // gate redirects TO them, so gating them would loop). The loaders read the
  // workspace from the param and re-assert the gate server-authoritatively.
  {
    path: 'w/:workspaceId/onboarding',
    lazy: () =>
      import('@/pages/onboarding/welcome').then((m) => ({
        loader: m.onboardingWelcomeLoader,
        Component: m.OnboardingWelcomePage,
      })),
    errorElement: <RouteErrorPage />,
  },
  {
    path: 'w/:workspaceId/onboarding/wizard',
    lazy: () =>
      import('@/pages/onboarding/wizard').then((m) => ({
        loader: m.onboardingWizardLoader,
        Component: m.OnboardingWizardPage,
      })),
    errorElement: <RouteErrorPage />,
  },
  {
    // Company-onboarding wizard (IMM-322). Gated to members with company-update;
    // the loader re-asserts the gate (member onboarding first, then company) and
    // bounces anyone who shouldn't be here.
    path: 'w/:workspaceId/onboarding/company',
    lazy: () =>
      import('@/pages/onboarding/company/wizard').then((m) => ({
        loader: m.companyOnboardingWizardLoader,
        Component: m.CompanyOnboardingWizardPage,
      })),
    errorElement: <RouteErrorPage />,
  },
  {
    // Un-bypassable blocking screen for a member who cannot edit the company
    // while its onboarding is in progress (IMM-322). The loader re-checks the
    // gate so they can never linger here once it no longer applies.
    path: 'w/:workspaceId/onboarding/company-blocked',
    lazy: () =>
      import('@/pages/onboarding/company/blocked').then((m) => ({
        loader: m.companyOnboardingBlockedLoader,
        Component: m.CompanyOnboardingBlockedPage,
      })),
    errorElement: <RouteErrorPage />,
  },
  {
    // Bare `/` (and any in-app entry without a workspace) resolves the workspace
    // to pre-select and forwards to `/w/:workspaceId`.
    index: true,
    loader: rootRedirect,
  },
  {
    path: 'w/:workspaceId',
    loader: requireWorkspace,
    hydrateFallbackElement: <LoadingPage />,
    errorElement: <RouteErrorPage />,
    element: <WorkspaceLayout />,
    children: [
      {
        index: true,
        loader: workspaceHomeLoader,
        shouldRevalidate: dashboardShouldRevalidate,
        lazy: () => import('@/pages/workspace/dashboard/dashboard-page').then((m) => ({ Component: m.DashboardPage })),
      },
      {
        path: 'hlre',
        children: [
          {
            index: true,
            loader: hlreAreaLoader,
            lazy: () => import('@/pages/workspace/hlre/setup').then((m) => ({ Component: m.HlreAreaSetupPage })),
          },
        ],
      },
      {
        path: 'locations',
        lazy: () =>
          import('@/pages/workspace/organisations').then((m) => ({
            loader: m.workspaceOrganisationsLoader,
            Component: m.WorkspaceOrganisationsPage,
          })),
      },
      {
        id: 'workspace-organisation-detail',
        path: 'locations/:id',
        lazy: () =>
          import('@/pages/workspace/organisation-detail').then((m) => ({
            loader: m.workspaceOrganisationDetailLoader,
            Component: m.WorkspaceOrganisationDetailPage,
          })),
        children: [
          {
            path: 'edit',
            lazy: () =>
              import('@/pages/workspace/organisation-edit').then((m) => ({
                Component: m.WorkspaceOrganisationEditPage,
              })),
          },
        ],
      },
      {
        id: 'workspace-organisation-review',
        path: 'locations/:id/review',
        lazy: () =>
          import('@/pages/workspace/organisation-review').then((m) => ({
            loader: m.workspaceOrganisationReviewLoader,
            Component: m.WorkspaceOrganisationReviewPage,
          })),
      },
      {
        path: 'members',
        lazy: () =>
          import('@/pages/workspace/members').then((m) => ({
            loader: m.workspaceMembersLoader,
            Component: m.WorkspaceMembersPage,
          })),
      },
      {
        id: 'workspace-member-detail',
        path: 'members/:id',
        lazy: () =>
          import('@/pages/workspace/member-detail').then((m) => ({
            loader: m.workspaceMemberDetailLoader,
            Component: m.WorkspaceMemberDetailPage,
          })),
        children: [
          {
            path: 'edit',
            lazy: () => import('@/pages/workspace/member-edit').then((m) => ({ Component: m.WorkspaceMemberEditPage })),
          },
        ],
      },
      {
        path: 'members/:id/profile/edit',
        lazy: () =>
          import('@/pages/workspace/member-profile-edit').then((m) => ({
            loader: m.memberProfileEditLoader,
            Component: m.MemberProfileEditPage,
          })),
      },
      {
        path: 'activities',
        lazy: () =>
          import('@/pages/workspace/activities/list').then((m) => ({
            loader: m.workspaceActivitiesLoader,
            Component: m.WorkspaceActivitiesPage,
          })),
      },
      // „Meine Aufgaben" is retired (IMM-776.3) — the path stays only as a
      // replacing redirect to its successor, so an old bookmark or deep link
      // still lands somewhere real.
      { path: 'tasks', element: <Navigate to="../activities" replace /> },
      // A TOP-LEVEL segment rather than a child of `acquisition-pipeline`
      // (IMM-430.7): nested, the literal `overview` would compete with
      // `:acquisitionId` for the same match.
      {
        path: 'deal-overview',
        // The view toggle is client-side grouping carried in the URL (IMM-629);
        // without this, switching it re-reads the whole unpaginated overview.
        lazy: () =>
          import('@/pages/workspace/deals/overview').then((m) => ({
            loader: m.dealOverviewLoader,
            shouldRevalidate: m.dealOverviewShouldRevalidate,
            Component: m.WorkspaceDealOverviewPage,
          })),
      },
      // The Aufträge list (IMM-1152): top-level like `deal-overview`, it spans deals.
      {
        path: 'mandates',
        lazy: () =>
          import('@/pages/workspace/mandates/list').then((m) => ({
            loader: m.workspaceMandatesLoader,
            Component: m.WorkspaceMandatesPage,
          })),
      },
      // The member's cross-deal Bewertungen (IMM-467.1). Top-level for the same
      // reason `deal-overview` is: it spans deals rather than hanging off one.
      {
        path: 'valuations',
        lazy: () =>
          import('@/pages/workspace/valuations/list').then((m) => ({
            loader: m.workspaceValuationsLoader,
            Component: m.WorkspaceValuationsPage,
          })),
      },
      {
        id: 'workspace-valuation-detail',
        path: 'valuations/:id',
        lazy: () =>
          import('@/pages/workspace/valuations/detail').then((m) => ({
            loader: m.workspaceValuationDetailLoader,
            Component: m.WorkspaceValuationDetailPage,
          })),
      },
      {
        path: 'chat',
        loader: chatLoader,
        lazy: () => import('@/pages/workspace/chat').then((m) => ({ Component: m.WorkspaceChatPage })),
      },
      // Posteingang / Postausgang are the same page bound to a different segment —
      // the segment is the route, so each is linkable and back-navigable. An opened
      // conversation is a route of its own under its segment, for the same reason:
      // a thread is linkable, and Back closes it instead of leaving the mailbox.
      {
        path: 'calendar',
        loader: calendarHomeLoader,
        lazy: () => import('@/pages/workspace/calendar/calendar-home').then((m) => ({ Component: m.CalendarHomePage })),
      },
      {
        path: 'mail',
        loader: mailboxHomeLoader,
        lazy: () =>
          import('@/pages/workspace/mailbox/mailbox-home').then((m) => ({
            element: <m.MailboxHomePage segment="posteingang" />,
          })),
      },
      {
        path: 'mail/sent',
        loader: mailboxHomeLoader,
        lazy: () =>
          import('@/pages/workspace/mailbox/mailbox-home').then((m) => ({
            element: <m.MailboxHomePage segment="postausgang" />,
          })),
      },
      {
        path: 'mail/sent/:providerThreadId',
        loader: mailboxHomeLoader,
        lazy: () =>
          import('@/pages/workspace/mailbox/mailbox-home').then((m) => ({
            element: <m.MailboxHomePage segment="postausgang" />,
          })),
      },
      // Ranked BELOW `mail/sent` by the router (a static segment outscores a
      // dynamic one), so the Postausgang is never swallowed by this.
      {
        path: 'mail/:providerThreadId',
        loader: mailboxHomeLoader,
        lazy: () =>
          import('@/pages/workspace/mailbox/mailbox-home').then((m) => ({
            element: <m.MailboxHomePage segment="posteingang" />,
          })),
      },
      {
        path: 'contacts',
        lazy: () =>
          import('@/pages/workspace/contacts/list').then((m) => ({
            loader: m.workspaceContactsLoader,
            Component: m.WorkspaceContactsPage,
          })),
      },
      {
        id: 'workspace-contact-detail',
        path: 'contacts/:id',
        lazy: () =>
          import('@/pages/workspace/contacts/detail').then((m) => ({
            loader: m.workspaceContactDetailLoader,
            Component: m.WorkspaceContactDetailPage,
          })),
      },
      {
        path: 'companies',
        lazy: () =>
          import('@/pages/workspace/companies/list').then((m) => ({
            loader: m.workspaceCompaniesLoader,
            Component: m.WorkspaceCompaniesPage,
          })),
      },
      {
        id: 'workspace-company-detail',
        path: 'companies/:id',
        lazy: () =>
          import('@/pages/workspace/companies/detail').then((m) => ({
            loader: m.workspaceCompanyDetailLoader,
            Component: m.WorkspaceCompanyDetailPage,
          })),
        errorElement: <RouteErrorPage />,
      },
      {
        path: 'properties',
        lazy: () =>
          import('@/pages/workspace/properties').then((m) => ({
            loader: m.workspacePropertiesLoader,
            Component: m.WorkspacePropertiesPage,
          })),
      },
      {
        id: 'workspace-property-detail',
        path: 'properties/:id',
        lazy: () =>
          import('@/pages/workspace/property-detail').then((m) => ({
            loader: m.workspacePropertyDetailLoader,
            shouldRevalidate: m.propertyDetailShouldRevalidate,
            Component: m.WorkspacePropertyDetailPage,
          })),
      },
      {
        path: 'leads/owners',
        lazy: () =>
          import('@/pages/workspace/leads/list').then((m) => ({
            loader: m.workspaceLeadsLoader,
            Component: m.WorkspaceLeadsPage,
          })),
      },
      {
        id: 'workspace-lead-detail',
        path: 'leads/owners/:id',
        lazy: () =>
          import('@/pages/workspace/leads/detail').then((m) => ({
            loader: m.workspaceLeadDetailLoader,
            Component: m.WorkspaceLeadDetailPage,
          })),
      },
      {
        path: 'acquisition-pipeline',
        lazy: () =>
          import('@/pages/workspace/deals/list').then((m) => ({
            loader: m.workspaceDealsLoader,
            Component: m.WorkspaceDealsPage,
          })),
      },
      {
        path: 'sales-pipeline',
        lazy: () =>
          import('@/pages/workspace/sales-pipeline/list').then((m) => ({
            loader: m.workspaceSalesPipelineLoader,
            shouldRevalidate: m.salesPipelineShouldRevalidate,
            Component: m.WorkspaceSalesPipelinePage,
          })),
      },
      {
        id: 'workspace-sales-pipeline-prospect',
        path: 'sales-pipeline/:prospectId',
        lazy: () =>
          import('@/pages/workspace/sales-pipeline/detail').then((m) => ({
            loader: m.workspaceSalesPipelineProspectLoader,
            Component: m.WorkspaceSalesPipelineProspectPage,
          })),
      },
      {
        id: 'workspace-deal-detail',
        path: 'acquisition-pipeline/:acquisitionId',
        lazy: () =>
          import('@/pages/workspace/deals/detail').then((m) => ({
            loader: m.workspaceDealDetailLoader,
            Component: m.WorkspaceDealDetailPage,
          })),
      },
      {
        // The Bewertung surface (IMM-456.1) — a sibling of the deal detail, on the
        // SAME loader: it needs the deal and the Objekt for its context, and sharing
        // the read is what stops the two routes disagreeing about deal state.
        id: 'workspace-deal-valuation',
        path: 'acquisition-pipeline/:acquisitionId/valuation',
        lazy: async () => {
          const [{ workspaceDealDetailLoader }, { WorkspaceDealValuationPage }] = await Promise.all([
            import('@/pages/workspace/deals/detail'),
            import('@/pages/workspace/deals/valuation'),
          ]);

          return { loader: workspaceDealDetailLoader, Component: WorkspaceDealValuationPage };
        },
      },
      {
        path: 'settings',
        loader: async ({ params }: LoaderFunctionArgs) => {
          await loadWorkspaceContext(params.workspaceId);

          return null;
        },
        lazy: () => import('@/pages/settings/settings-layout').then((m) => ({ Component: m.SettingsLayout })),
        children: [
          {
            index: true,
            loader: ({ params }: LoaderFunctionArgs) => redirect(appRoutes.settingsMyProfile(params.workspaceId!)),
          },
          {
            path: 'my-profile',
            lazy: () => import('@/pages/settings/my-profile').then((m) => ({ Component: m.SettingsMyProfilePage })),
          },
          {
            path: 'my-profile/edit',
            lazy: () =>
              import('@/pages/settings/my-profile-edit').then((m) => ({ Component: m.SettingsMyProfileEditPage })),
          },
          {
            path: 'mailbox',
            lazy: () => import('@/pages/settings/mailbox').then((m) => ({ Component: m.SettingsMailboxPage })),
          },
          {
            path: 'company-mailboxes',
            lazy: () =>
              import('@/pages/settings/company-mailboxes').then((m) => ({ Component: m.SettingsCompanyMailboxesPage })),
          },
          {
            path: 'company',
            lazy: () => import('@/pages/settings/company').then((m) => ({ Component: m.SettingsCompanyPage })),
          },
          {
            path: 'branding',
            lazy: () => import('@/pages/settings/branding').then((m) => ({ Component: m.SettingsBrandingPage })),
          },
          {
            path: 'templates',
            lazy: () =>
              import('@/pages/settings/document-templates').then((m) => ({
                Component: m.SettingsDocumentTemplatesPage,
              })),
          },
          {
            path: 'templates/new/:draftId',
            lazy: () =>
              import('@/pages/settings/new-document-template').then((m) => ({
                Component: m.SettingsNewDocumentTemplatePage,
              })),
          },
          {
            path: 'templates/email/new',
            lazy: () =>
              import('@/pages/settings/new-email-template').then((m) => ({
                Component: m.SettingsNewEmailTemplatePage,
              })),
          },
          {
            path: 'templates/email/:emailTemplateId',
            lazy: () =>
              import('@/pages/settings/email-template').then((m) => ({ Component: m.SettingsEmailTemplatePage })),
          },
          {
            path: 'templates/buyer-contract/adapt',
            lazy: () =>
              import('@/pages/settings/buyer-contract-template-adapt').then((m) => ({
                Component: m.SettingsBuyerContractTemplateAdaptPage,
              })),
          },
          {
            path: 'templates/buyer-contract/:buyerContractTemplateId',
            lazy: () =>
              import('@/pages/settings/buyer-contract-template').then((m) => ({
                Component: m.SettingsBuyerContractTemplatePage,
              })),
          },
          {
            path: 'templates/:documentTemplateId',
            lazy: () =>
              import('@/pages/settings/document-template').then((m) => ({ Component: m.SettingsDocumentTemplatePage })),
          },
          {
            path: 'portals',
            lazy: () => import('@/pages/settings/portals').then((m) => ({ Component: m.SettingsPortalsPage })),
          },
          {
            path: 'lead-partners',
            lazy: () =>
              import('@/pages/settings/lead-partners').then((m) => ({ Component: m.SettingsLeadPartnersPage })),
          },
          {
            path: 'admins',
            lazy: () => import('@/pages/settings/admins').then((m) => ({ Component: m.SettingsAdminsPage })),
          },
          {
            path: 'api-keys',
            lazy: () => import('@/pages/settings/api-keys').then((m) => ({ Component: m.SettingsApiKeysPage })),
          },
          { path: 'mcp', lazy: () => import('@/pages/settings/mcp').then((m) => ({ Component: m.SettingsMcpPage })) },
          {
            path: 'skills',
            lazy: () => import('@/pages/settings/skills').then((m) => ({ Component: m.SettingsSkillsPage })),
          },
          // The handoff's entry point (IMM-751.4). A STABLE route: the linked
          // application sends the member back here, so the crossing closes
          // where it opened. Reachable by URL even where no application is
          // configured — the page says so; the navigation row is what vanishes.
          {
            path: 'print-material',
            lazy: () =>
              import('@/pages/settings/print-material').then((m) => ({ Component: m.SettingsPrintMaterialPage })),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

/**
 * Every route hangs off one pathless root whose `errorElement` is the floor for
 * the whole tree (ADR 0014 — the per-route boundary, applied once at the top
 * instead of route by route). A route that declares no `errorElement` of its own
 * — the index redirect, the `*` catch-all — bubbles here instead of falling
 * through to React Router's built-in error view, which renders the raw
 * `error.stack` in a `<pre>` **in production builds too**. Routes that need a
 * different surface still override it; being pathless, this root changes no
 * child's path.
 */
export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: topLevelRoutes,
  },
];
